import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import React, { forwardRef, useState, useEffect } from "react";
import { ITheme, useTheme } from "src/theme";
import Icon from "@components/Icon";
import { SCREEN_SIZE } from "@static/screen-size";

const RadioButtonDescription = styled.div<
  Partial<RadioButtonProps> & { isSelected: boolean }
>`
  display: flex;
  flex-direction: column;
  // gap: 24px 0;
  color: ${(props) =>
    props.isSelected && props.activeType === "fill" && props.isHoverAlt
      ? "#FFF"
      : !props.isSelected && props.isHoverAlt
      ? "#8A8988"
      : "#434343"};
  text-align: ${(props) => (props.textAlign ? props.textAlign : "left")};
  h4 {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
  }
  @media screen and (max-width: 768px) {
    h4 {
      font-size: 16px;
    }
  }
`;

const RadioButtonGroupContainer = styled.div<Partial<RadioButtonGroupProps>>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  align-items: center;
  padding: ${(props) => (props.padding ? props.padding : "0px")};
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    gap: 0 24px;
    width: auto;
    flex-direction: row;
  }
`;

const RadioButtonContainer = styled.button<
  Partial<RadioButtonProps> & { isSelected: boolean }
>`
  display: flex;
  width: ${(props) => (props.width ? props.width + "px" : "100%")};
  height: ${(props) => (props.height ? props.height + "px" : "auto")};
  border: none;
  position: relative;
  flex-direction: ${(props) =>
    props.iconALign === "top"
      ? "column"
      : props.iconALign === "bottom"
      ? "column-reverse"
      : props.iconALign === "left"
      ? "row"
      : "row-reverse"};
  align-items: ${(props) => (props.textAlign ? props.textAlign : "left")};
  gap: 13px;
  background-color: ${(props) =>
    props.isSelected && props.activeType === "fill" && props.isHoverAlt
      ? "#883E89"
      : props.isSelected && props.activeType === "fill" && !props.isHoverAlt
      ? "#F2E6F1"
      : props.isSelected
      ? "#FFF"
      : "#F5F5F5"};
  border-radius: ${({ borderRadius }) =>
    borderRadius ? borderRadius : "32px"};
  cursor: ${(props) =>
    props.disabled ? "not-allowed" : "pointer"};
  box-shadow: ${(props) =>
    props.isSelected ? "0px 1px 20px rgba(0, 0, 0, 0.1)" : ""};
  &:hover {
    background-color: ${(props) =>
      props.isSelected &&
      props.activeType === "fill" &&
      props.isHoverAlt &&
      !props.disabled
        ? "#883E89"
        : props.isSelected &&
          props.activeType === "fill" &&
          !props.isHoverAlt &&
          !props.disabled
        ? "#F2E6F1"
        : props.isSelected && !props.disabled
        ? "#FFF"
        : "#F5F5F5"};
    box-shadow: ${(props) =>
      !props.disabled ? "0px 1px 20px rgba(0, 0, 0, 0.1)" : ""};
  }
  padding: ${(props) => (props.padding ? props.padding : "24px")};

  justify-content: ${(props) =>
    props.justifyContent ? props.justifyContent : "unset"};

  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    display: inline-flex;
    gap: 24px 24px;
    padding: ${(props) => (props.padding ? props.padding : "36px")};
  }
  transition: all 0.2s linear;
  align-self: stretch;
`;

const Header = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  color: #883e89;
  white-space: break-spaces;
`;

const RadioBtnHovering = css`
  &:hover {
    background-color: #fff;
    box-shadow: 0px 1px 20px rgba(0, 0, 0, 0.1);
    // p,h4{
    //   color:#883E89;
    // }
  }
`;

const RadioBtnHoveringAlt = css`
  &:hover {
    background-color: #fff;
    box-shadow: 0px 4px 35px rgba(0, 0, 0, 0.08);
    p,
    h4 {
      color: #783780;
    }
  }
`;

export interface RadioButtonProps {
  header?: string;
  text?: string;
  onChange: (value: string | number | undefined) => void;
  value: string | number | boolean | undefined;
  selected?: string | number | null;
  activeType?: "outline" | "fill";
  widthIcon?: boolean;
  textAlign?: "left" | "center" | "right";
  iconALign?: "top" | "bottom" | "left" | "right";
  iconActive?: string;
  iconInactive?: string;
  iconHover?: string;
  name?: string;
  padding?: string;
  width?: number;
  height?: number;
  justifyContent?: string;
  dateText?: string;
  children?: React.ReactNode;
  isHoverAlt?: boolean;
  borderRadius?: string;
  disabled?: boolean;
}

const RadioButton = ({
  name,
  activeType,
  textAlign,
  iconALign,
  header,
  text,
  onChange,
  value,
  selected,
  iconActive,
  iconInactive,
  iconHover,
  padding,
  justifyContent,
  width,
  height,
  dateText,
  children,
  borderRadius,
  disabled,
  ...props
}: RadioButtonProps) => {
  const isSelected = selected === value;
  const [isHover, setIsHover] = useState<boolean>(false);
  return (
    <RadioButtonContainer
      className={
        disabled
          ? ""
          : isHover && props.isHoverAlt
          ? RadioBtnHoveringAlt
          : isHover
          ? RadioBtnHovering
          : ""
      }
      name={name}
      activeType={activeType}
      iconALign={iconALign}
      header={header}
      text={text}
      width={width}
      height={height}
      onMouseOver={(e: any) => {
        if (disabled) return;
        if (!isSelected) {
          setIsHover(true);
        }
      }}
      onMouseLeave={(e: any) => {
        if (disabled) return;
        setIsHover(false);
      }}
      onClick={(e: any) => {
        if (disabled) return;
        setIsHover(false);
        onChange({ ...e, target: { ...e.target, value, name } });
      }}
      isSelected={isSelected}
      padding={padding}
      justifyContent={justifyContent}
      isHoverAlt={props.isHoverAlt}
      borderRadius={borderRadius}
      disabled={disabled}
    >
      {props.widthIcon && (
        <Icon
          name={isSelected ? iconActive : isHover ? iconHover : iconInactive}
        />
      )}
      <RadioButtonDescription
        isSelected={isSelected}
        textAlign={textAlign}
        activeType={activeType}
        isHoverAlt={props.isHoverAlt}
      >
        {header && <Header>{header}</Header>}
        {children && children}
      </RadioButtonDescription>
    </RadioButtonContainer>
  );
};

RadioButton.defaultProps = {
  text: "",
  selected: null,
  vaue: undefined,
  dateText: "",
  onChange: () => {},
};

export type RadioButtonGroupProps = Omit<
  RadioButtonProps,
  "header" | "text" | "onChange" | "value" | "selected" | "dateText"
> & {
  name?: string;
  children?: React.ReactNode;
  onChange?: (e: any) => void;
  value: string | number | null;
  theme?: ITheme;
};

const RadioButtonGroup = forwardRef<HTMLDivElement, RadioButtonGroupProps>(
  (
    {
      value,
      onChange,
      children,
      name,
      padding,
      ...props
    }: RadioButtonGroupProps,
    ref
  ) => {
    const theme = useTheme();
    const [selected, setSelected] = useState<string | number | null>(value);

    useEffect(() => {
      setSelected(value);
    }, [value]);

    const onChangeRadio = (event: any) => {
      const value = event.target.value;
      setSelected(value);
      if (onChange) {
        onChange(event);
      }
    };
    return (
      <RadioButtonGroupContainer theme={theme} ref={ref} padding={padding}>
        {React.Children.toArray(children).map((item: any, index: number) => {
          return (
            item.type === RadioButton && (
              <RadioButton
                {...props}
                name={name}
                key={`aiva-${index}-radio`}
                header={item.props.header}
                text={item.props.text}
                value={item.props.value}
                dateText={item.props.dateText}
                selected={selected}
                onChange={onChangeRadio}
                iconActive={item.props.iconActive}
                iconInactive={item.props.iconInactive}
                iconHover={item.props.iconHover}
                padding={item.props.padding}
                justifyContent={item.props.justifyContent}
                children={item.props.children}
                disabled={item.props.disabled}
              />
            )
          );
        })}
      </RadioButtonGroupContainer>
    );
  }
);

RadioButtonGroup.defaultProps = {
  widthIcon: true,
  textAlign: "left",
};
export { RadioButtonGroup, RadioButton };
